import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, RefreshCw, BarChart3, Users, Coins } from 'lucide-react';
import { getPlatformMetrics } from '@/lib/platformAdminApi';
import { toast } from 'sonner';

export const PlatformMetricsCard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await getPlatformMetrics();
      if (response.success && response.agent_run) {
        // Extract metrics from Hermes response
        const toolCalls = response.agent_run.tool_calls || [];
        const metricsTool = toolCalls.find(t => t.tool_name === 'get_platform_metrics');
        
        if (metricsTool && metricsTool.result) {
          setMetrics(metricsTool.result);
        }
      }
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      toast.error('Failed to load platform metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return (
    <Card className="card-hover-lift" data-testid="platform-metrics-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="heading-font">Platform Metrics</CardTitle>
            <CardDescription>Aggregate statistics across all hotels</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMetrics}
            disabled={loading}
            data-testid="refresh-metrics-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : metrics ? (
          <div className="space-y-4">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Hotels */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <p className="text-sm font-medium text-blue-900">Total Hotels</p>
                </div>
                <p className="text-3xl font-bold heading-font text-blue-900 tabular-nums">
                  {metrics.total_hotels || 0}
                </p>
                <p className="text-xs text-blue-700 mt-1">Enrolled in platform</p>
              </div>

              {/* Total Staked */}
              <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="h-5 w-5 text-amber-600" />
                  <p className="text-sm font-medium text-amber-900">Total Staked</p>
                </div>
                <p className="text-3xl font-bold heading-font text-amber-900 tabular-nums">
                  {(metrics.total_staked_hermes || 0).toLocaleString()}
                </p>
                <p className="text-xs text-amber-700 mt-1">HERMES tokens</p>
              </div>

              {/* Average Score */}
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                  <p className="text-sm font-medium text-green-900">Avg Tier Score</p>
                </div>
                <p className="text-3xl font-bold heading-font text-green-900 tabular-nums">
                  {metrics.average_tier_score?.toFixed(1) || '0.0'}
                </p>
                <p className="text-xs text-green-700 mt-1">Platform average</p>
              </div>
            </div>

            {/* Tier Distribution */}
            {metrics.tier_distribution && Object.keys(metrics.tier_distribution).length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Tier Distribution</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(metrics.tier_distribution).map(([tier, count]) => (
                    <Badge
                      key={tier}
                      variant="outline"
                      className="text-sm"
                    >
                      {tier}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Active Agents */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Active AI Agents</p>
                <Badge variant="secondary" className="tabular-nums">
                  {metrics.active_agents || 0} agents
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Bitsy, Marina, and Sage across all hotels
              </p>
            </div>

            {/* Timestamp */}
            {metrics.timestamp && (
              <p className="text-xs text-muted-foreground text-right">
                Updated: {new Date(metrics.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No metrics available</p>
            <Button onClick={fetchMetrics} variant="outline" size="sm">
              Load Metrics
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformMetricsCard;
